/**
 * Theme background — central registry of presets + the applyThemeBg helper.
 *
 * Browser quirk we work around: `background-image` only accepts gradient
 * and `url()` values. A trailing flat hex (e.g. `radial-gradient(...), #1a0510`)
 * is INVALID for background-image — the whole declaration gets rejected.
 * So each preset splits its visual into:
 *   - `bg`         — flat color (applied to `background-color`)
 *   - `bgImage`    — gradients only, NO trailing hex (applied to `background-image`)
 *   - `tilePreview`— full shorthand including color (used for the picker tile)
 *
 * We change two CSS variables on document.documentElement so all surfaces
 * that opt-in (via Tailwind arbitrary-value syntax `bg-[var(--app-bg)]`
 * or the global body CSS) can update instantly without React state plumbing.
 */

export const DEFAULT_BG = "default";

// Helper to keep gradient stacks DRY across presets.
const TRANSPARENT_GRADIENT = "linear-gradient(180deg, transparent, transparent)";

export const BG_PRESETS = [
  {
    slug: "default",
    name: "Obsidian",
    bg: "#0a0814",
    bgImage:
      "radial-gradient(ellipse at top, rgba(217,70,239,0.18), transparent 55%), " +
      "radial-gradient(ellipse at bottom right, rgba(245,158,11,0.08), transparent 55%)",
    tilePreview:
      "radial-gradient(ellipse at top, rgba(217,70,239,0.18), transparent 55%), " +
      "radial-gradient(ellipse at bottom right, rgba(245,158,11,0.08), transparent 55%), #0a0814",
  },
  {
    slug: "midnight",
    name: "Midnight Blue",
    bg: "#06122b",
    bgImage: "radial-gradient(ellipse at top, rgba(56,189,248,0.22), transparent 55%)",
    tilePreview: "radial-gradient(ellipse at top, rgba(56,189,248,0.22), transparent 55%), #06122b",
  },
  {
    slug: "forest",
    name: "Forest",
    bg: "#06150f",
    bgImage: "radial-gradient(ellipse at top, rgba(16,185,129,0.22), transparent 55%)",
    tilePreview: "radial-gradient(ellipse at top, rgba(16,185,129,0.22), transparent 55%), #06150f",
  },
  {
    slug: "sunset",
    name: "Sunset",
    bg: "#1a0510",
    bgImage:
      "radial-gradient(ellipse at top, rgba(249,115,22,0.30), transparent 55%), " +
      "radial-gradient(ellipse at bottom, rgba(217,70,239,0.20), transparent 55%)",
    tilePreview:
      "radial-gradient(ellipse at top, rgba(249,115,22,0.30), transparent 55%), " +
      "radial-gradient(ellipse at bottom, rgba(217,70,239,0.20), transparent 55%), #1a0510",
  },
  {
    slug: "pure-black",
    name: "Pure Black",
    bg: "#000000",
    bgImage: TRANSPARENT_GRADIENT,
    tilePreview: "#000000",
  },
  {
    slug: "graphite",
    name: "Graphite",
    bg: "#15171c",
    bgImage: "linear-gradient(180deg, #1c1e23 0%, #0e1014 100%)",
    tilePreview: "linear-gradient(180deg, #1c1e23 0%, #0e1014 100%)",
  },
  {
    slug: "rose",
    name: "Rose",
    bg: "#1a0612",
    bgImage: "radial-gradient(ellipse at top, rgba(244,114,182,0.30), transparent 60%)",
    tilePreview: "radial-gradient(ellipse at top, rgba(244,114,182,0.30), transparent 60%), #1a0612",
  },
  {
    slug: "amber",
    name: "Amber",
    bg: "#1a1206",
    bgImage: "radial-gradient(ellipse at top, rgba(251,191,36,0.25), transparent 60%)",
    tilePreview: "radial-gradient(ellipse at top, rgba(251,191,36,0.25), transparent 60%), #1a1206",
  },
];

/** Apply a background — accepts either a preset slug OR a raw hex string.
 *  Sets `--app-bg` (color) + `--app-bg-image` (gradients only) on <html>,
 *  plus a `data-theme` attribute so global CSS can opt-in to per-theme
 *  overrides if needed.
 *
 *  Note: `--app-bg-image` MUST contain only gradient/url() values — a
 *  trailing hex would make the entire `background-image` rule invalid in
 *  the browser. See the file header for the bug we worked around. */
export function applyThemeBg(value) {
  if (typeof document === "undefined") return;
  const html = document.documentElement;
  const preset = BG_PRESETS.find((p) => p.slug === value);
  // Custom hex path: only a color, no gradient.
  const isHex = !preset && typeof value === "string" && value.startsWith("#");
  const bg = preset ? preset.bg : (isHex ? value : "#0a0814");
  const bgImage = preset ? preset.bgImage : TRANSPARENT_GRADIENT;
  html.style.setProperty("--app-bg", bg);
  html.style.setProperty("--app-bg-image", bgImage);
  html.setAttribute("data-theme", preset?.slug || (isHex ? "custom" : "default"));
}

/** Compat shim — old code used `preview`; keep alias so any callers that
 *  haven't been updated yet still render. New code should use `tilePreview`. */
BG_PRESETS.forEach((p) => { p.preview = p.tilePreview; });
